import { useState } from "react";
import Helpers from "../../Config/Helpers";
import { Link, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Box,
  Avatar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  Chip,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import { resolveLogoSrc } from "../../utils/resolveLogoSrc";

// Derives initials from a name string
const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

function Header({ collapsed }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const shopLogoSrc =
    resolveLogoSrc(Helpers.authShop?.logo) ||
    require("../../images/logo-light.png");

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleClose();
    localStorage.clear();
    Helpers.toast("success", "Logged out successfully");
    navigate("/");
  };

  const user = Helpers.authUser;
  const isPrimary = user.parent_id === 0;
  const roleName = isPrimary ? "Primary Account" : user.user_role?.name;
  const initials = getInitials(user.name);

  return (
    <AppBar
      id="page-topbar"
      elevation={0}
      sx={{
        background: "#ffffff",
        borderBottom: "1px solid #e9ecf0",
        boxShadow: "0 1px 8px rgba(102,126,234,0.08)",
        color: "#344054",
        position: "fixed",
        right: 0,
        top: 0,
        zIndex: (theme) => theme.zIndex.drawer - 1,
        "@media print": {
          display: "none",
        },
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 2, sm: 3 },
          justifyContent: "space-between",
        }}
      >
        {/* Left side — show shop logo here when sidebar is collapsed */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {collapsed && (
            <Link
              to="/user/dashboard"
              replace
              style={{ display: "flex", alignItems: "center" }}
            >
              <img
                src={shopLogoSrc}
                alt="logo"
                style={{
                  height: 40,
                  opacity: collapsed ? 1 : 0,
                  transform: collapsed ? "translateX(0)" : "translateX(-12px)",
                  transition: "opacity 0.25s ease, transform 0.25s ease",
                  pointerEvents: collapsed ? "auto" : "none",
                }}
              />
            </Link>
          )}
        </Box>
        <Box />

        {/* Right side — profile section */}
        <Box
          onClick={handleOpen}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
            borderRadius: "12px",
            px: 1.5,
            py: 0.75,
            transition: "background 0.18s ease",
            "&:hover": {
              background: "rgba(102, 126, 234, 0.07)",
            },
          }}
        >
          {/* Avatar — uses photo if available, else initials with gradient */}
          <Avatar
            src={
              user.profile_photo
                ? `${Helpers.baseUrl}/storage/${user.profile_photo}`
                : undefined
            }
            sx={{
              width: 38,
              height: 38,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              fontSize: "0.875rem",
              fontWeight: 700,
              color: "#fff",
              boxShadow: "0 2px 8px rgba(102,126,234,0.35)",
            }}
          >
            {initials}
          </Avatar>

          {/* Name + role — hidden on small screens */}
          <Box sx={{ display: { xs: "none", md: "block" }, textAlign: "left" }}>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#1a1f36",
                lineHeight: 1.3,
              }}
            >
              {user.name}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.72rem",
                color: "#667eea",
                fontWeight: 500,
                lineHeight: 1.2,
              }}
            >
              {roleName}
            </Typography>
          </Box>

          {/* Chevron indicator */}
          <Box
            sx={{
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              color: "#98a2b3",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
              fontSize: 18,
            }}
          >
            ▾
          </Box>
        </Box>
      </Toolbar>

      {/* ── Profile Dropdown Menu ── */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: "14px",
            border: "1px solid #e9ecf0",
            boxShadow: "0 8px 32px rgba(102,126,234,0.15)",
            overflow: "visible",
            // Arrow pointer
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: -6,
              right: 22,
              width: 12,
              height: 12,
              background: "#fff",
              border: "1px solid #e9ecf0",
              borderBottom: "none",
              borderRight: "none",
              transform: "rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
      >
        {/* User info header inside dropdown */}
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1.5,
            background: "linear-gradient(135deg, #667eea08 0%, #764ba210 100%)",
            borderRadius: "14px 14px 0 0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={
                user.profile_photo
                  ? `${Helpers.baseUrl}/storage/${user.profile_photo}`
                  : undefined
              }
              sx={{
                width: 46,
                height: 46,
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                fontSize: "1rem",
                fontWeight: 700,
                color: "#fff",
                boxShadow: "0 3px 10px rgba(102,126,234,0.35)",
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography
                sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#1a1f36" }}
              >
                {user.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "#667788",
                  wordBreak: "break-all",
                }}
              >
                {user.email}
              </Typography>
              <Chip
                icon={
                  isPrimary ? (
                    <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 13 }} />
                  ) : (
                    <PersonOutlineOutlinedIcon sx={{ fontSize: 13 }} />
                  )
                }
                label={roleName}
                size="small"
                sx={{
                  mt: 0.5,
                  height: 20,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  background:
                    "linear-gradient(135deg, #667eea18 0%, #764ba218 100%)",
                  color: "#667eea",
                  border: "1px solid #667eea30",
                  "& .MuiChip-icon": { color: "#667eea" },
                }}
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "#e9ecf0" }} />

        {/* Profile Settings */}
        <MenuItem
          component={Link}
          to="/user/profile-settings"
          onClick={handleClose}
          sx={{
            mx: 1,
            mt: 0.5,
            borderRadius: "8px",
            py: 1,
            gap: 0,
            "&:hover": { background: "rgba(102,126,234,0.07)" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 34 }}>
            <SettingsOutlinedIcon sx={{ fontSize: 18, color: "#667eea" }} />
          </ListItemIcon>
          <Typography
            sx={{ fontSize: "0.875rem", color: "#344054", fontWeight: 500 }}
          >
            Profile Settings
          </Typography>
        </MenuItem>

        <Divider sx={{ borderColor: "#e9ecf0", mx: 1, my: 0.5 }} />

        {/* Logout */}
        <MenuItem
          onClick={handleLogout}
          sx={{
            mx: 1,
            mb: 0.5,
            borderRadius: "8px",
            py: 1,
            "&:hover": { background: "rgba(234,102,102,0.07)" },
          }}
        >
          <ListItemIcon sx={{ minWidth: 34 }}>
            <LogoutOutlinedIcon sx={{ fontSize: 18, color: "#e53e3e" }} />
          </ListItemIcon>
          <Typography
            sx={{ fontSize: "0.875rem", color: "#e53e3e", fontWeight: 500 }}
          >
            Logout
          </Typography>
        </MenuItem>
      </Menu>
    </AppBar>
  );
}

export default Header;
